'use client'

import { Box, Stack } from '@mantine/core'
import { DataTable, DataTableSortStatus } from 'mantine-datatable'
import { useParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { memo, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import IconCaretUpDown from '@/assets/icons/caret-updown.svg'
import IconCaretUpDownDown from '@/assets/icons/caret-updown-down.svg'
import Pagination from '@/components/basic/Pagination'
import { SectionErrorBoundary } from '@/components/error/boundary'
import useSubscribeLiveCoinsMarket from '@/hooks/business/useSubscribeLiveCoinsMarket'
import { tokenizedStocksLocales } from '@/i18n/locales'
import { useI18nRouter } from '@/i18n/navigation'
import { isInIframe } from '@/utils/browser'

import {
  COINS_OG_PAGE_SIZE,
  COINS_PAGE_SIZE,
  CoinTab,
  TOKENIZED_STOCKS_PAGE_SIZE,
} from '../configs'
import { useCoinsTableStore } from '../context'
import { defaultColumnProps, type TableRecord, useColumns } from './columns/configs'
import EmptyState from './EmptyState'
import FavoritesTabView from './FavoritesTabView'
import { useCoinRestrictedAreas } from './hooks/useCoinRestrictedAreas'
import useTableData from './hooks/useTableData'
import styles from './styles.module.css'

let isFirstRender = true

export default memo(TableWrapper)

function TableWrapper({
  isOGPrice,
  coinColumnTitle,
}: {
  isOGPrice?: boolean
  coinColumnTitle?: string
}) {
  return (
    <SectionErrorBoundary>
      <Table isOGPrice={isOGPrice} coinColumnTitle={coinColumnTitle} />
    </SectionErrorBoundary>
  )
}

function Table({ isOGPrice, coinColumnTitle }: { isOGPrice?: boolean; coinColumnTitle?: string }) {
  const router = useI18nRouter()
  const sortIconColor = isOGPrice ? 'var(--content-active)' : 'var(--content-primary)'
  const { locale } = useParams<{ locale: string }>()
  const tHeaders = useTranslations('coinTable.headers')
  const isTokenizedStocksLocale = (tokenizedStocksLocales as string[]).includes(locale)
  const resolvedCoinColumnTitle =
    coinColumnTitle ?? (isTokenizedStocksLocale ? tHeaders('asset') : undefined)

  const {
    currentCoinTab,
    currentPage,
    updateFilters,
    updateLiveCoinsMarket,
    clearLiveCoinsMarket,
  } = useCoinsTableStore(
    useShallow(
      ({
        currentCoinTab,
        currentPage,
        updateFilters,
        updateLiveCoinsMarket,
        clearLiveCoinsMarket,
      }) => ({
        currentCoinTab,
        currentPage,
        updateFilters,
        updateLiveCoinsMarket,
        clearLiveCoinsMarket,
      }),
    ),
  )

  // Scroll to the table top when search params change after the first render
  // The effect will be re-executed after query params change due to the route navigation
  useEffect(() => {
    if (!isFirstRender) {
      document
        .getElementById('trending-bottom')
        ?.scrollIntoView({ block: 'start', behavior: 'instant' })
    }
    isFirstRender = false
  }, [])

  const {
    isPending,
    records,
    totalRecords,
    handleSortStatusChange,
    sortStatus,
    tokenizedStockSlugSet,
  } = useTableData({ isOGPrice })

  const isFavoritesTab = currentCoinTab.value === CoinTab.Favorites

  useSubscribeLiveCoinsMarket(!isFavoritesTab ? records.map(coin => coin.slug) : [], {
    updateLiveCoinsMarket,
    clearLiveCoinsMarket,
  })

  // Favorites tab only show 6 records to construct an appropriate table height
  const tableRecords = isFavoritesTab ? records.slice(0, 6) : records

  // Fetch restricted_areas and visibility for all visible coins using batch API
  const { restrictedAreasMap, visibleCoins } = useCoinRestrictedAreas(tableRecords)

  const columns = useColumns(
    restrictedAreasMap,
    isOGPrice,
    resolvedCoinColumnTitle,
    currentCoinTab.value === CoinTab.TokenizedStocks,
    [CoinTab.AllCoins, CoinTab.AllAssets].includes(currentCoinTab.value)
      ? tokenizedStockSlugSet
      : undefined,
  )

  if (visibleCoins.length === 0 && !isFavoritesTab) {
    return <EmptyState />
  }

  return (
    <Box pos="relative">
      {isFavoritesTab && <FavoritesTabView />}
      <Stack gap="16px">
        <DataTable
          fetching={isPending}
          textSelectionDisabled
          records={visibleCoins}
          columns={columns}
          defaultColumnProps={defaultColumnProps}
          idAccessor="token_id"
          pinFirstColumn
          /* Sort */
          sortStatus={sortStatus as DataTableSortStatus<TableRecord>}
          sortIcons={{
            sorted: <IconCaretUpDownDown width={14} fill={sortIconColor} />,
            unsorted: <IconCaretUpDown width={14} fill={sortIconColor} />,
          }}
          onSortStatusChange={handleSortStatusChange}
          /* Row Click */
          onRowClick={({ record }) => {
            if (isOGPrice && isInIframe()) {
              window.parent.postMessage(
                {
                  type: 'syncPricePagePathname',
                  payload: {
                    pathname: `/en/og/price/${record.slug}`,
                  },
                },
                '*',
              )
            } else {
              const isTokenizedStock =
                currentCoinTab.value === CoinTab.TokenizedStocks ||
                ([CoinTab.AllCoins, CoinTab.AllAssets].includes(currentCoinTab.value) &&
                  tokenizedStockSlugSet.has(record.slug))
              const path = isOGPrice
                ? `/og/price/${record.slug}`
                : isTokenizedStock
                  ? `/stocks/${record.slug}`
                  : `/${record.slug}`
              router.push(path)
            }
          }}
          /* Styling */
          classNames={{
            ...styles,
            header:
              currentCoinTab.value === CoinTab.TokenizedStocks
                ? `${styles.header} ${styles.tokenizedStocksHeader}`
                : styles.header,
          }}
          minHeight="500px"
          backgroundColor="var(--bg-app)"
          withRowBorders={false}
          highlightOnHover
          highlightOnHoverColor="var(--bg-surface-secondary)"
          className={isOGPrice ? styles.ogCoinTable : void 0}
        />
        {!isFavoritesTab && (
          <Pagination
            totalRecords={totalRecords}
            recordsPerPage={
              isOGPrice
                ? COINS_OG_PAGE_SIZE
                : currentCoinTab.value === CoinTab.TokenizedStocks
                  ? TOKENIZED_STOCKS_PAGE_SIZE
                  : COINS_PAGE_SIZE
            }
            page={currentPage}
            onChange={page => {
              updateFilters({ currentPage: page })
            }}
            // Align the next control to the right edge of the main content area
            mr={isOGPrice ? void 0 : { contentLimit: -18 }}
            isOGPrice={isOGPrice}
          />
        )}
      </Stack>
    </Box>
  )
}
